// Git repositories (/v1/repositories). A repository holds the git history of
// a team's report project; each of its branches shows as a workspace. A hosted
// repository takes pushes over HTTPS at cloneUrl; a mirror follows a repository
// on a git forge and is read-only here. These routes exist only while the
// server has repositories on, so callers that only add a control treat a
// failure as "no repositories".

import { getJSON, sendJSON, request, query } from "./http.js";
import type { GitSyncStatus } from "./git.js";

const BASE = "/v1/repositories";

/** RepositoryOrigin says where the history is written: here, or on a forge. */
export type RepositoryOrigin = "hosted" | "mirror";

export interface RepositoryInfo {
  id: string;
  teamId: string;
  name: string;
  origin: RepositoryOrigin;
  defaultBranch: string;
  /** The forge URL; present on a mirror only. */
  repoUrl?: string;
  /** The HTTPS URL to clone from and, when hosted, to push to. */
  cloneUrl: string;
  sizeBytes: number;
  /** Over its size limit: reads and builds work; until it is below the
   * limits again, only changes to the default branch that do not make it
   * larger are accepted, and deletes of other branches. */
  overQuota: boolean;
  lastSyncAt: string;
  lastSyncStatus: GitSyncStatus | "";
  lastSyncError: string;
  /** Forge branches that got no workspace because of the branch cap. */
  skippedBranches: number;
  created: string;
  updated: string;
}

/** BranchInfo is one branch workspace of a repository. */
export interface BranchInfo {
  workspaceId: string;
  branch: string;
  pinned: boolean;
  lastPushAt: string;
  /** The commit the workspace's files show. */
  commit: string;
}

export type RepositoryDetail = RepositoryInfo & { branches: BranchInfo[] };

/** listRepositories returns the team's repositories (viewer+). */
export async function listRepositories(teamId: string): Promise<RepositoryInfo[]> {
  const { items } = await getJSON<{ items: RepositoryInfo[] }>(BASE + query({ team: teamId }));
  return items;
}

/** getRepository returns one repository with its branch workspaces (viewer+). */
export function getRepository(id: string): Promise<RepositoryDetail> {
  return getJSON<RepositoryDetail>(`${BASE}/${encodeURIComponent(id)}`);
}

// ----- Secrets (write-only; values are never returned) -----

/** RepositorySecretScope says where a secret the repository sees is defined. */
export type RepositorySecretScope = "repository" | "team";

export interface RepositorySecretName {
  name: string;
  scope: RepositorySecretScope;
  /** Builds ignore the secret: the cluster reserves that name. */
  reserved?: boolean;
}

/**
 * listRepositorySecrets returns the NAMES every branch workspace of the
 * repository inherits (viewer+): the repository's own secrets plus the team
 * secrets it does not shadow.
 */
export async function listRepositorySecrets(id: string): Promise<RepositorySecretName[]> {
  const { items } = await getJSON<{ items: RepositorySecretName[] }>(
    `${BASE}/${encodeURIComponent(id)}/secrets`,
  );
  return items;
}

/** setRepositorySecret creates or rotates a REPOSITORY secret (member+). */
export async function setRepositorySecret(id: string, name: string, value: string): Promise<void> {
  await sendJSON(`${BASE}/${encodeURIComponent(id)}/secrets`, "POST", { name, value });
}

/** deleteRepositorySecret removes one REPOSITORY secret by name (member+). */
export async function deleteRepositorySecret(id: string, name: string): Promise<void> {
  await request(`${BASE}/${encodeURIComponent(id)}/secrets/${encodeURIComponent(name)}`, {
    method: "DELETE",
  });
}

// ----- Export -----

/** RepositoryExport is the downloaded zip, named as the server suggests. */
export interface RepositoryExport {
  blob: Blob;
  filename: string;
}

/**
 * exportRepository downloads the repository as a zip. It answers 409
 * repository_empty before the first commit and 429 export_busy while too many
 * exports run.
 */
export async function exportRepository(id: string): Promise<RepositoryExport> {
  const res = await request(`${BASE}/${encodeURIComponent(id)}/export`);
  const named = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") ?? "");
  return { blob: await res.blob(), filename: named?.[1] ?? `repository-${id}.zip` };
}
